import { TYPES_SIGNALEMENT, type CodeSecteur, type TypeSignalement } from '../domaine/types'
import { SIGNALEMENT_AIDE, SIGNALEMENT_LIBELLE, T } from '../domaine/libelles'
import { libelleSecteur } from '../domaine/secteurs'
import { creerSignalement, patrouilleActive } from '../noyau/actions'
import { redimensionner } from '../noyau/photo'
import { zonage } from '../noyau/zonage'
import { el, vibrer, vider } from '../ui/dom'
import { ouvrirFeuille } from '../ui/feuille'
import { montrerToast } from '../ui/toast'

/*
  Écran Signaler.

  Quatre grands boutons, puis trois champs au maximum : secteur, quantité,
  précision. La photo est en plus, et toujours facultative.

  Le secteur est pré-rempli avec celui de la patrouille en cours quand il y en
  a une : à 22 h, une touche de moins compte.
*/

export function monterSignaler(hote: HTMLElement): () => void {
  let vivant = true

  async function rendreChoix(): Promise<void> {
    if (!vivant) return
    vider(hote)

    const titre = el('section', { class: 'entete-simple' }, [
      el('h1', { class: 'entete-simple__titre', text: T.signalerTitre }),
      el('p', { class: 'entete-simple__aide', text: T.signalerAide }),
    ])

    const grille = el('div', { class: 'grille-signalement' })
    for (const type of TYPES_SIGNALEMENT) {
      const bouton = el('button', { type: 'button', class: 'carte-signalement' }, [
        el('span', { class: 'carte-signalement__titre', text: SIGNALEMENT_LIBELLE[type] }),
        el('span', { class: 'carte-signalement__aide', text: SIGNALEMENT_AIDE[type] }),
      ])
      bouton.addEventListener('click', () => {
        vibrer(15)
        void rendreFormulaire(type)
      })
      grille.append(bouton)
    }

    hote.append(titre, grille)
  }

  async function rendreFormulaire(type: TypeSignalement): Promise<void> {
    if (!vivant) return

    const secteurs = await zonage()
    const patrouille = await patrouilleActive()
    let secteur: CodeSecteur = patrouille?.secteur ?? secteurs[0]?.code ?? ''
    let photo: Blob | null = null

    vider(hote)

    const boutonSecteur = el('button', { type: 'button', class: 'champ-bouton' })
    const majSecteur = () => {
      boutonSecteur.textContent = libelleSecteur(secteur, secteurs)
    }
    majSecteur()
    boutonSecteur.addEventListener('click', () => {
      ouvrirFeuille({
        titre: T.choisirSecteur,
        aide: T.choisirSecteurAide,
        options: secteurs.map((s) => ({
          cle: s.code,
          titre: s.libelle,
          sousTitre: s.groupe ?? undefined,
        })),
        surChoix: (code) => {
          secteur = code
          majSecteur()
        },
      })
    })

    const quantite = el('input', {
      type: 'number',
      class: 'champ',
      min: 1,
      step: 1,
      inputmode: 'numeric',
      placeholder: '1',
      'aria-label': T.combien,
    }) as HTMLInputElement

    const note = el('input', {
      type: 'text',
      class: 'champ',
      maxlength: 200,
      autocomplete: 'off',
      'aria-label': T.note,
    }) as HTMLInputElement

    const etatPhoto = el('div', { class: 'photo__etat', text: T.photoAide })

    const fichier = el('input', {
      type: 'file',
      accept: 'image/*',
      class: 'photo__entree',
      hidden: true,
      // `capture` ouvre directement l'appareil photo sur Android.
      capture: 'environment',
    }) as HTMLInputElement

    const boutonPhoto = el('button', {
      type: 'button',
      class: 'champ-bouton',
      text: T.prendrePhoto,
    })
    boutonPhoto.addEventListener('click', () => fichier.click())

    fichier.addEventListener('change', () => {
      const source = fichier.files?.[0]
      if (!source) return
      etatPhoto.textContent = '…'
      void redimensionner(source)
        .then((blob) => {
          photo = blob
          etatPhoto.textContent = `${Math.round(blob.size / 1024)} Ko · sans métadonnées`
          boutonPhoto.textContent = T.retirerPhoto
          boutonPhoto.onclick = () => {
            photo = null
            fichier.value = ''
            etatPhoto.textContent = T.photoAide
            boutonPhoto.textContent = T.prendrePhoto
            boutonPhoto.onclick = () => fichier.click()
          }
        })
        .catch(() => {
          // Image illisible : on continue sans photo, la saisie reste valable.
          photo = null
          etatPhoto.textContent = T.photoAide
        })
    })

    const enregistrer = el('button', {
      type: 'button',
      class: 'bouton-plein bouton-plein--grand',
      text: T.envoyerSignalement,
    })
    enregistrer.addEventListener('click', () => {
      vibrer(15)
      const n = Number.parseInt(quantite.value, 10)
      void creerSignalement({
        type,
        secteur,
        quantite: Number.isFinite(n) && n > 0 ? n : null,
        note: note.value || null,
        photo,
      }).then(() => {
        montrerToast(T.signalementFait)
        void rendreChoix()
      })
    })

    const retour = el('button', { type: 'button', class: 'lien-retour', text: '‹ Signaler' })
    retour.addEventListener('click', () => void rendreChoix())

    hote.append(
      el('section', { class: 'entete-simple' }, [
        retour,
        el('h1', { class: 'entete-simple__titre', text: SIGNALEMENT_LIBELLE[type] }),
        el('p', { class: 'entete-simple__aide', text: SIGNALEMENT_AIDE[type] }),
      ]),
      el('div', { class: 'formulaire' }, [
        bloc(T.secteur, boutonSecteur),
        bloc(T.combien.toUpperCase(), quantite, T.noteAide),
        bloc(T.note.toUpperCase(), note, T.noteAide),
        bloc(T.photo.toUpperCase(), boutonPhoto, undefined, [fichier, etatPhoto]),
        enregistrer,
      ]),
    )
  }

  function bloc(
    label: string,
    controle: HTMLElement,
    aide?: string,
    extras: HTMLElement[] = [],
  ): HTMLElement {
    return el('div', { class: 'bloc-champ' }, [
      el('label', { class: 'champ__label', text: label }),
      controle,
      ...extras,
      aide ? el('p', { class: 'champ__aide', text: aide }) : null,
    ])
  }

  void rendreChoix()

  return () => {
    vivant = false
  }
}
